// passwordStrength.ts
//
// How guessable an account password is, for Sign up and Reset password
// (Phase 12.5; requirements: Auth - Password Policy; decisions 14.2, item 7).
//
// ---------------------------------------------------------------------------
// A RECOGNISED ESTIMATOR, NOT HOME-MADE RULES. zxcvbn (maintained as
// @zxcvbn-ts, MIT) models how passwords are actually cracked: it finds common
// passwords, dictionary words and names, keyboard runs (qwerty), repeats and
// sequences (aaaa, 1234), dates and years, and - given the email being signed
// up with - variations of that address, and scores 0 to 4 by the guesses they
// would take. Composition rules ("a digit and a letter") are the thing it
// replaces: `Password1234` passes them and is among the first guesses tried.
// Length beats composition here for the reason 6.1 gave for the passphrase.
//
// THE BAR IS A SCORE OF 3, together with the 12 characters the server enforces.
// zxcvbn's 3 is "safely unguessable: moderate protection from an offline
// slow-hash attack" - the case that matters if a database copy ever leaks,
// since online guessing is already slowed by the captcha and GoTrue's limits.
//
// A CLIENT-SIDE COURTESY, AND SAID SO. The server enforces the length and the
// character types (`minimum_password_length` and `password_requirements`,
// passwordPolicy.ts). GoTrue has nothing that blocks trivial patterns, so a
// caller that skips these pages skips this check. That is the honest statement
// of the requirement, and why its old "even if client validation is bypassed"
// wording for patterns was dropped rather than kept as a promise nothing keeps.
//
// ITS DICTIONARIES ARE LARGE (several MB unpacked), so nothing here imports them
// at load. `loadPasswordEstimator()` fetches them the first time a password page
// asks, as a chunk of its own; no other page ever downloads them.
//
// THE WORDS ARE OURS. zxcvbn's own feedback sentences are not used - a short,
// plain line is chosen here from the pattern it found, and a refusal on submit
// goes through errorCodes.ts (PASSWORD_TOO_GUESSABLE), like every other refusal.
// ---------------------------------------------------------------------------

import { MIN_PASSWORD_LENGTH } from "./passwordPolicy";

/** The lowest zxcvbn score an account password may have. */
export const MIN_PASSWORD_SCORE = 3;

export const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Strong", "Very strong"] as const;

export type Score = 0 | 1 | 2 | 3 | 4;

/** The parts of a zxcvbn result this module reads. */
export type EstimateResult = {
  score: number;
  sequence: { pattern: string; dictionaryName?: string; regexName?: string }[];
};

export type PasswordVerdict = {
  score: Score;
  label: (typeof STRENGTH_LABELS)[number];
  /** One line when the password would be refused; null when it is fine. */
  advice: string | null;
  acceptable: boolean;
};

export const PASSWORD_ADVICE = {
  short: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
  email: "Don't build it from your email address.",
  common: "Common words, names and passwords are easy to guess.",
  keyboard: "Keyboard runs like qwerty are easy to guess.",
  repeat: "Repeated characters are easy to guess.",
  sequence: "Runs like abc or 1234 are easy to guess.",
  date: "Dates and years are easy to guess.",
  more: "Add another word or two.",
} as const;

/**
 * The email being signed up with, as the pieces a password might be built
 * from: the whole address, the part before @, the words in it, and the domain's
 * name. All handed to zxcvbn as "user inputs", which it treats like a
 * dictionary it knows the attacker has.
 */
export function inputsFromEmail(email: string | null | undefined): string[] {
  const address = (email ?? "").trim().toLowerCase();
  if (!address) return [];
  const [local = "", domain = ""] = address.split("@");
  // The words run together too: measured, "sheldonhall2026" scored an
  // acceptable 3 for sheldon.hall@... until the joined form was an input.
  const parts: string[] = [address, local, local.replace(/[._+\-\d]+/g, ""), ...local.split(/[._+\-\d]+/), domain.split(".")[0] ?? ""];
  return [...new Set(parts.filter((p) => p.length >= 3))];
}

/** Why a password is weak, in one line, from the first pattern zxcvbn found in it. */
function adviceFor(result: EstimateResult): string {
  for (const match of result.sequence) {
    if (match.pattern === "dictionary" && match.dictionaryName === "userInputs") return `${PASSWORD_ADVICE.email} ${PASSWORD_ADVICE.more}`;
  }
  for (const match of result.sequence) {
    switch (match.pattern) {
      case "dictionary":
        return `${PASSWORD_ADVICE.common} ${PASSWORD_ADVICE.more}`;
      case "spatial":
        return `${PASSWORD_ADVICE.keyboard} ${PASSWORD_ADVICE.more}`;
      case "repeat":
        return `${PASSWORD_ADVICE.repeat} ${PASSWORD_ADVICE.more}`;
      case "sequence":
        return `${PASSWORD_ADVICE.sequence} ${PASSWORD_ADVICE.more}`;
      case "date":
        return `${PASSWORD_ADVICE.date} ${PASSWORD_ADVICE.more}`;
      case "regex":
        if (match.regexName === "recentYear") return `${PASSWORD_ADVICE.date} ${PASSWORD_ADVICE.more}`;
    }
  }
  return PASSWORD_ADVICE.more;
}

/** What the meter under the box shows: one bar, filled `fill` percent, and a label. */
export type MeterReading = {
  score: Score;
  label: (typeof STRENGTH_LABELS)[number];
  advice: string | null;
  fill: number;
};

/**
 * The meter's reading while the password is typed - from the first character,
 * not only once the rules are met.
 *
 * CAPPED AT "Fair" UNTIL EVERY RULE IS TICKED. "correct horse battery staple"
 * is very hard to guess and has no capital, number or symbol; a meter saying
 * "Very strong" beside unticked rules says the password is fine when the page
 * will refuse it. So the bar climbs as the password gets harder to guess, and
 * only turns green, "Strong" or better, once the checklist is complete.
 *
 * The bar is never empty once something is typed: "Very weak" fills a fifth.
 * The "use at least 12 characters" advice is left out - the checklist above
 * already says it.
 */
export function meterReading(verdict: PasswordVerdict, rulesMet: boolean): MeterReading {
  const score = (rulesMet ? verdict.score : Math.min(verdict.score, MIN_PASSWORD_SCORE - 1)) as Score;
  return {
    score,
    label: STRENGTH_LABELS[score],
    advice: verdict.advice === PASSWORD_ADVICE.short ? null : verdict.advice,
    fill: ((score + 1) / STRENGTH_LABELS.length) * 100,
  };
}

/** The verdict for one password, from zxcvbn's result. Pure, so a suite can hold it. */
export function verdictFor(password: string, result: EstimateResult): PasswordVerdict {
  const score = Math.max(0, Math.min(4, Math.round(result.score))) as Score;
  const longEnough = password.length >= MIN_PASSWORD_LENGTH;
  const acceptable = longEnough && score >= MIN_PASSWORD_SCORE;

  return {
    score,
    label: STRENGTH_LABELS[score],
    acceptable,
    advice: acceptable ? null : !longEnough ? PASSWORD_ADVICE.short : adviceFor(result),
  };
}

export type PasswordEstimator = (password: string, userInputs?: string[]) => PasswordVerdict;

let loading: Promise<PasswordEstimator> | null = null;

/**
 * The estimator, with its dictionaries - fetched the first time it is asked for
 * and shared after. If the fetch fails the promise rejects and the next call
 * tries again; a page that cannot load it falls back to the server's rule, the
 * length, rather than refusing everyone.
 */
export function loadPasswordEstimator(): Promise<PasswordEstimator> {
  loading ??= (async () => {
    const [{ ZxcvbnFactory }, common, en] = await Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
      import("@zxcvbn-ts/language-en"),
    ]);
    const zxcvbn = new ZxcvbnFactory({
      dictionary: { ...common.dictionary, ...en.dictionary },
      graphs: common.adjacencyGraphs,
      translations: en.translations,
    });
    return (password: string, userInputs: string[] = []) => verdictFor(password, zxcvbn.check(password, userInputs));
  })().catch((e) => {
    loading = null;
    throw e;
  });
  return loading;
}
